<template>
    <section class="timesheet-object-access">
        <h2>Доступ по объектам</h2>
        <p>Персональные разрешения дополняют общие права ниже и действуют на все месяцы объекта.
            Редактирование включает просмотр, отправку на проверку и подтверждение прибытия.
            Закрытые табели остаются защищены от изменений.</p>
        <label for="timesheet-access-object">Объект</label>
        <v-select
            v-model="buildingId"
            input-id="timesheet-access-object"
            :options="objectOptions"
            :reduce="option => option.value"
            label="text"
            placeholder="Найти объект по названию"
        >
            <template #no-options>Объекты не найдены</template>
        </v-select>
        <template v-if="buildingId">
            <div class="timesheet-object-access__add">
                <div>
                    <label for="timesheet-access-user">Добавить пользователя</label>
                    <v-select
                        v-model="userId"
                        input-id="timesheet-access-user"
                        :options="availableUsers"
                        :reduce="option => option.value"
                        label="text"
                        placeholder="Найти по ФИО"
                    >
                        <template #no-options>Нет подходящих пользователей для добавления</template>
                    </v-select>
                </div>
                <b-button variant="outline-primary" :disabled="!userId" @click="addUser">
                    Добавить
                </b-button>
            </div>
            <p v-if="!objectGrants.length" class="text-muted">
                Персональных разрешений нет. Действуют общие настройки доступа.
            </p>
            <div v-for="grant in objectGrants" :key="grant.user_id" class="timesheet-object-access__row">
                <strong>{{ userName(grant.user_id) }}</strong>
                <b-form-select
                    :value="grant.level"
                    :options="levels"
                    :aria-label="`Доступ: ${userName(grant.user_id)}`"
                    @input="level => changeLevel(grant.user_id, level)"
                ></b-form-select>
                <b-button variant="outline-danger" size="sm"
                          :aria-label="`Убрать персональный доступ: ${userName(grant.user_id)}`"
                          @click="removeUser(grant.user_id)">
                    Убрать
                </b-button>
            </div>
            <p class="timesheet-object-access__hint">
                Удаление персонального разрешения или выбор «Просмотр» не отменяют более широкие
                права из общих настроек. Изменения вступят в силу после нажатия «Сохранить» вверху страницы.
            </p>
        </template>
    </section>
</template>

<script>
export default {
    name: 'TimesheetObjectAccess',
    props: {
        value: {type: Array, default: () => []},
        objects: {type: Array, default: () => []},
        users: {type: Array, default: () => []}
    },
    data() {
        return {
            buildingId: null,
            userId: null,
            levels: [
                {value: 'view', text: 'Просмотр'},
                {value: 'edit', text: 'Редактирование и отправка'}
            ]
        };
    },
    computed: {
        objectOptions() {
            const counts = this.value.reduce((result, grant) => {
                result[grant.building_id] = (result[grant.building_id] || 0) + 1;
                return result;
            }, {});
            return this.objects.map(object => ({
                value: Number(object.building_id),
                text: `${object.name}${object.is_active ? '' : ' (неактивный)'}${
                    counts[object.building_id] ? ` · доступов: ${counts[object.building_id]}` : ''
                }`
            }));
        },
        objectGrants() {
            return this.value.filter(grant => Number(grant.building_id) === Number(this.buildingId));
        },
        availableUsers() {
            return this.users.filter(user => !this.objectGrants.some(grant => (
                Number(grant.user_id) === Number(user.value)
            )));
        }
    },
    watch: {
        buildingId() { this.userId = null; }
    },
    methods: {
        userName(id) {
            const user = this.users.find(item => Number(item.value) === Number(id));
            return user ? user.text : `Пользователь #${id}`;
        },
        addUser() {
            if (!this.buildingId || !this.userId
                || !this.availableUsers.some(user => Number(user.value) === Number(this.userId))) return;
            this.$emit('input', this.value.concat({
                building_id: Number(this.buildingId), user_id: Number(this.userId), level: 'view'
            }));
            this.userId = null;
        },
        changeLevel(userId, level) {
            if (!this.levels.some(option => option.value === level)) return;
            this.$emit('input', this.value.map(grant => (
                Number(grant.building_id) === Number(this.buildingId) && Number(grant.user_id) === Number(userId)
                    ? {...grant, level} : grant
            )));
        },
        removeUser(userId) {
            this.$emit('input', this.value.filter(grant => (
                Number(grant.building_id) !== Number(this.buildingId) || Number(grant.user_id) !== Number(userId)
            )));
        }
    }
};
</script>

<style scoped>
.timesheet-object-access {
    margin-bottom: 1.5rem;
    padding: 1rem;
    border: 1px solid #dbe4ec;
    border-radius: 12px;
}
.timesheet-object-access h2 { font-size: 1.15rem; }
.timesheet-object-access label { display: block; font-weight: 600; }
.timesheet-object-access__add {
    display: flex;
    align-items: flex-end;
    gap: .75rem;
    margin: 1rem 0;
}
.timesheet-object-access__add > div { flex: 1; min-width: 0; }
.timesheet-object-access__row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(240px, 1fr) auto;
    align-items: center;
    gap: .75rem;
    padding: .75rem 0;
    border-top: 1px solid #e3e9ef;
}
.timesheet-object-access__row strong { overflow-wrap: anywhere; }
.timesheet-object-access__hint { margin: 1rem 0 0; color: #607286; font-size: .9rem; }
@media (max-width: 760px) {
    .timesheet-object-access__row { grid-template-columns: minmax(0, 1fr) auto; }
    .timesheet-object-access__row strong { grid-column: 1 / -1; }
    .timesheet-object-access__row select { min-width: 0; font-size: .85rem; }
}
</style>
